import React from 'react'

import moment from 'moment'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import {
  Box,
  ButtonWithLoading,
  Flex,
} from 'app/components/design-system-components'
import Badge from 'app/components/design-system-components/Badges'
import * as snugLogger from 'app/services/snugLogger'
import Amenities from 'app/sm/common/amenities'
import {
  formatLeaseTerm,
  formatRent,
  NO_IMAGE_PLACEHOLDER_URL,
  urlTo,
} from 'app/sm/helpers'
import { RankPieChart } from 'app/sm/renter_applications/components/applications_card'
import { getApplyRouteUrl } from 'app/sm/utils/applyRouting'
import * as textHelpers from 'app/utils/text/helpers'

const StyledApplyAndBook = styled(Flex)`
  width: 100%;
  margin-top: 16px;
  button {
    width: 100%;
  }
  a {
    color: inherit;
    text-decoration: none;
    width: 100%;
    display: inline-block;
  }
`

// used public agency property list page
export class AgencyApplyCard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isApplyLoading: false,
    }
  }

  setFavorite(e) {
    e.preventDefault()
  }

  getOfferID = (property) => {
    const activeOffer = (property.offers || []).find((o) => o.isActive) || {}
    return activeOffer.guidID
  }

  _renderApplyButtonLabel = () => {
    const { property } = this.props
    const { applyButtonLabel = '' } = property || {}
    const DEFAULT_APPLY_LABEL = 'Apply'
    if (applyButtonLabel) {
      return applyButtonLabel
    }

    return DEFAULT_APPLY_LABEL
  }

  _renderBookButtonLabel = () => {
    const DEFAULT_BOOK_LABEL = 'Book'
    const { ballotsEnabled } = this.props
    if (ballotsEnabled) {
      return textHelpers.BALLOT_PROPERTY_APPLY_BUTTON_LABEL
    }
    return DEFAULT_BOOK_LABEL
  }

  handleApplyClick = async (e) => {
    e.preventDefault()

    // Set loading state
    this.setState({ isApplyLoading: true })

    const { property, applicationId } = this.props
    const offerId = this.getOfferID(property)
    const queryString = window.location.search

    // Build fallback V1 URL based on application status
    let fallbackUrl
    if (applicationId) {
      fallbackUrl = urlTo('application', { applicationId }) + queryString
    } else {
      fallbackUrl =
        urlTo('applyRedirect', { slug: property.slug }) + queryString
    }

    // Always call backend API to determine V2 vs V1 routing
    // Don't use property.applyLink as it may be outdated V1 URL
    try {
      const applyUrl = await getApplyRouteUrl(offerId, queryString, fallbackUrl)
      window.location.href = applyUrl
    } catch (err) {
      snugLogger.error('Error navigating to apply page:', err)
      this.setState({ isApplyLoading: false })
      // Fall back to V1 on error
      window.location.href = fallbackUrl
    }
  }

  render() {
    const {
      property,
      status,
      applicationId,
      score,
      weeklyRent,
      term,
      availableFromDate,
      loggedIn,
      teamSlug,
      ballotsEnabled,
    } = this.props
    const { isFavourite, applyLink = '' } = property || {}

    let imageUrl = NO_IMAGE_PLACEHOLDER_URL
    if (property.images && property.images.length > 0) {
      imageUrl = (
        property.images.find((image) => image.isMain === true) ||
        property.images[0]
      ).URL
    }

    let address = ''
    let suburb = ''
    if (property.address) {
      if (property.address.unitNumber) {
        address = `${property.address.unitNumber}/${property.address.streetNumber} ${property.address.streetName}`
      } else {
        address = `${property.address.streetNumber} ${property.address.streetName}`
      }
      suburb = property.address.suburb
    }

    const heartStyle = isFavourite
      ? 'icon-heart-filled red-color'
      : 'icon-heart-outline'
    const heartButton = loggedIn ? (
      <button onClick={(e) => this.setFavorite(e)}>
        <i className={heartStyle} />
      </button>
    ) : (
      ''
    )
    const viewPropertyDetailsTagClass =
      'rental-application-view-property-details'

    let applicationCorrespondingLink
    if (status === 'Offered') {
      applicationCorrespondingLink = urlTo('offeredApplication', {
        propertyId: property.guidID,
        applicationId,
      })
    } else if (status === 'Details') {
      applicationCorrespondingLink = urlTo('applyRedirect', {
        slug: property.slug,
      })
    } else {
      applicationCorrespondingLink = urlTo('application', { applicationId })
    }
    const availableFromFormatted = moment().isAfter(moment(availableFromDate))
      ? 'Available now'
      : moment(availableFromDate).format('DD MMM YYYY')
    const queryString = window.location.search
    applicationCorrespondingLink = applicationCorrespondingLink + queryString
    const finalApplyLink = applyLink
      ? applyLink + queryString
      : applicationCorrespondingLink
    const formattedRent = formatRent(weeklyRent)

    return (
      <div className="sm-properties-card-list-item">
        <div className="sm-properties-card-list-item-wrapper pb20">
          <Link
            to={applicationCorrespondingLink}
            className={`image ${viewPropertyDetailsTagClass}`}
            id={teamSlug}
            style={{ backgroundImage: `url(${imageUrl})` }}
          >
            <div className="controls">
              <div className="left">{false && heartButton}</div>
            </div>
          </Link>
          <div className="meta bbs">
            <div className="address">
              <Link
                to={applicationCorrespondingLink}
                className={`street ${viewPropertyDetailsTagClass}`}
                id={teamSlug}
                title={address}
              >
                {address}
              </Link>
              <div className="suburb mb10">{suburb}</div>
              <Amenities payload={property} wrapClassName="amenities-widget" />
            </div>

            {status !== 'Details' && (
              <div className="rank-chart">
                <div className="rank-chart-wrapper">
                  <RankPieChart rank={Math.round(score)} />
                  <div className="rank-chart-text fontSize-13">
                    {Math.round(score)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="info">
            <Flex alignItems="center">
              <span>{formattedRent}</span>{' '}
              <span>
                <i className="icon-dot" /> {formatLeaseTerm(term)} months
              </span>
              {ballotsEnabled && (
                <Badge ml={2} text="Ballots" variant="notice" />
              )}
            </Flex>
            <div>
              <span>Date Available : </span>{' '}
              <span>{availableFromFormatted}</span>
            </div>
            <StyledApplyAndBook>
              <Box mr="8px" width="50%">
                {!ballotsEnabled && (
                  <ButtonWithLoading
                    width="100%"
                    variant="solid"
                    onClick={this.handleApplyClick}
                    loading={this.state.isApplyLoading}
                  >
                    {this._renderApplyButtonLabel()}
                  </ButtonWithLoading>
                )}
              </Box>
              <Box ml="8px" width="50%">
                <ButtonWithLoading width="100%">
                  <Link
                    to={
                      ballotsEnabled
                        ? finalApplyLink
                        : applicationCorrespondingLink
                    }
                  >
                    {this._renderBookButtonLabel()}
                  </Link>
                </ButtonWithLoading>
              </Box>
            </StyledApplyAndBook>
          </div>
        </div>
      </div>
    )
  }
}
